import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

type Primitive = string | number | boolean | undefined | null;
type FilterShape = Record<string, Primitive>;

function toParam(value: Primitive): string | null {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'boolean') return value ? '1' : '0';
  return String(value);
}

function fromParam<T extends Primitive>(raw: string | null, defaultValue: T): T {
  if (raw === null) return defaultValue;
  if (typeof defaultValue === 'number') {
    const n = Number(raw);
    return (Number.isFinite(n) ? n : defaultValue) as T;
  }
  if (typeof defaultValue === 'boolean') {
    return (raw === '1' || raw === 'true') as T;
  }
  return raw as T;
}

export function useFilterParams<T extends FilterShape>(
  defaults: T,
  options?: { prefix?: string },
): [T, (patch: Partial<T>) => void, () => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const prefix = options?.prefix ?? '';

  const filters = useMemo(() => {
    const result = {} as T;
    (Object.keys(defaults) as Array<keyof T>).forEach((key) => {
      const paramKey = prefix + String(key);
      const raw = searchParams.get(paramKey);
      const defaultValue = defaults[key];
      result[key] = fromParam(raw, defaultValue as Primitive) as T[typeof key];
    });
    return result;
  }, [searchParams, defaults, prefix]);

  const setFilters = useCallback(
    (patch: Partial<T>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          (Object.keys(patch) as Array<keyof T>).forEach((key) => {
            const paramKey = prefix + String(key);
            const value = patch[key] as Primitive;
            const defaultValue = defaults[key];
            const valueStr = toParam(value);
            const defaultStr = toParam(defaultValue as Primitive);
            if (valueStr === null || valueStr === defaultStr) {
              next.delete(paramKey);
            } else {
              next.set(paramKey, valueStr);
            }
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, defaults, prefix],
  );

  const resetFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        (Object.keys(defaults) as Array<keyof T>).forEach((key) => {
          next.delete(prefix + String(key));
        });
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams, defaults, prefix]);

  return [filters, setFilters, resetFilters];
}

export default useFilterParams;
